import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import { createSha256Transformer, createTimestampTransformer } from '../common/sql';
import {
  DBDocumentPageSchema,
  DBDocumentIndexEntrySchema,
  DBDigitisationRunSchema,
  DBSourceDocumentSchema,
  DocumentPageSchema,
  DocumentIndexEntrySchema,
  DigitisationRunSchema,
  SourceDocumentSchema,
} from './schemas';

export const SourceDocumentCodecConfig: TransformerConfig<
  typeof SourceDocumentSchema,
  typeof DBSourceDocumentSchema
> = {
  name: 'SourceDocument',
  clientSchema: SourceDocumentSchema,
  dbSchema: DBSourceDocumentSchema,
  transformers: [
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>('sha256'),
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'contentFingerprint',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const DocumentPageCodecConfig: TransformerConfig<
  typeof DocumentPageSchema,
  typeof DBDocumentPageSchema
> = {
  name: 'DocumentPage',
  clientSchema: DocumentPageSchema,
  dbSchema: DBDocumentPageSchema,
  transformers: [
    createTimestampTransformer<typeof DocumentPageSchema, typeof DBDocumentPageSchema>('createdAt'),
  ],
};

export const DocumentIndexEntryCodecConfig: TransformerConfig<
  typeof DocumentIndexEntrySchema,
  typeof DBDocumentIndexEntrySchema
> = {
  name: 'DocumentIndexEntry',
  clientSchema: DocumentIndexEntrySchema,
  dbSchema: DBDocumentIndexEntrySchema,
  transformers: [
    createTimestampTransformer<typeof DocumentIndexEntrySchema, typeof DBDocumentIndexEntrySchema>(
      'createdAt'
    ),
  ],
};

export const DigitisationRunCodecConfig: TransformerConfig<
  typeof DigitisationRunSchema,
  typeof DBDigitisationRunSchema
> = {
  name: 'DigitisationRun',
  clientSchema: DigitisationRunSchema,
  dbSchema: DBDigitisationRunSchema,
  transformers: [
    createSha256Transformer<typeof DigitisationRunSchema, typeof DBDigitisationRunSchema>(
      'configHash'
    ),
    createTimestampTransformer<typeof DigitisationRunSchema, typeof DBDigitisationRunSchema>(
      'startedAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof DigitisationRunSchema, typeof DBDigitisationRunSchema>(
      'finishedAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof DigitisationRunSchema, typeof DBDigitisationRunSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof DigitisationRunSchema, typeof DBDigitisationRunSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const encodeSourceDocumentToDb = createEncodeToDbFn(SourceDocumentCodecConfig);
export const decodeSourceDocumentFromDb = createDecodeFromDbFn(SourceDocumentCodecConfig);
export const encodeDocumentPageToDb = createEncodeToDbFn(DocumentPageCodecConfig);
export const decodeDocumentPageFromDb = createDecodeFromDbFn(DocumentPageCodecConfig);
export const encodeDocumentIndexEntryToDb = createEncodeToDbFn(DocumentIndexEntryCodecConfig);
export const decodeDocumentIndexEntryFromDb = createDecodeFromDbFn(DocumentIndexEntryCodecConfig);
export const encodeDigitisationRunToDb = createEncodeToDbFn(DigitisationRunCodecConfig);
export const decodeDigitisationRunFromDb = createDecodeFromDbFn(DigitisationRunCodecConfig);
